import classNames from 'classnames';
import { CircleXIcon } from 'lucide-react';
import { InputHTMLAttributes, useRef } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  className?: string;
  inputClassName?: string;
  error?: string | boolean;
  label?: string | React.ReactElement;
  labelIcon?: React.ReactElement;
  icon?: React.ReactElement;
  labelRef?: React.RefObject<HTMLLabelElement | null>;
  ref?: React.RefObject<HTMLInputElement | null>;
  onIconClick?: (event: React.MouseEvent) => void;
  clearable?: boolean;
  focusOnClear?: boolean;
}

function Input({
  className,
  inputClassName,
  value,
  label,
  error,
  disabled = false,
  labelIcon,
  icon,
  labelRef,
  ref: inputRef,
  onIconClick,
  clearable,
  focusOnClear = true,
  ...rest
}: InputProps) {
  const _inputRef = useRef<HTMLInputElement | null>(null);
  const showClearDataIcon: boolean = !!value && (clearable || !rest.readOnly) && !disabled;
  return (
    <div className={classNames('mb-2', className)}>
      <label
        className={classNames(
          'text-heading flex flex-col font-semibold',
          label && error && 'text-red-600'
        )}
        ref={labelRef}
      >
        <div className="mb-1 flex min-h-5 items-center gap-1 leading-none">
          {labelIcon && <div>{labelIcon}</div>}
          {label}
        </div>
        <div className="relative">
          <input
            className={classNames(
              'h-11 w-full rounded border border-neutral-500 bg-white px-2 font-normal outline-offset-1 outline-none',
              icon && showClearDataIcon && 'pr-[72px]',
              icon && !showClearDataIcon && 'pr-[54px]',
              !icon && showClearDataIcon && 'pr-[25px]',
              !icon && !showClearDataIcon && 'pr-[8px]',

              disabled && 'border-neutral-500/30 text-neutral-500',
              error && 'border-red-600 text-red-600',
              'focus:outline-4 focus:outline-blue-200 focus:outline-solid',
              inputClassName
            )}
            value={value ?? ''}
            disabled={disabled}
            ref={inputRef || _inputRef}
            {...rest}
          />
          {icon && (
            <div
              className={classNames(
                'absolute right-1 bottom-0 flex h-11 w-[45px] items-center justify-center border-l',
                !error && 'border-neutral-500',
                error && 'border-red-600'
              )}
              role="button"
              onClick={onIconClick}
            >
              {icon}
            </div>
          )}

          {showClearDataIcon && (
            <div
              className={classNames(
                'absolute bottom-0 flex h-11 w-[25px] items-center justify-center',
                icon ? 'right-[48px]' : 'right-0',
                'text-neutral-500 hover:text-neutral-800'
              )}
              role="button"
              onClick={(event) => {
                event.preventDefault();
                if (rest.onChange) {
                  // @ts-expect-error Not all target event properties are set
                  rest.onChange({ target: { name: rest.name ?? '', value: '' } });

                  if (focusOnClear) {
                    inputRef?.current?.focus();
                    _inputRef?.current?.focus();
                  }
                }
              }}
            >
              <CircleXIcon size={15} />
            </div>
          )}
        </div>
      </label>
      {error && typeof error === 'string' && (
        <div className="mt-2 rounded bg-red-50 px-2 py-1 text-red-700">{error}</div>
      )}
    </div>
  );
}

export default Input;
